import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ReactNode, useEffect } from 'react';
import { Button } from '../..';
import Badgev2 from '../badge/Badgev2';
import Menu from '../menu/Menu';
import CheckboxFilter from './CheckboxFilter';
import DateFilter from './DateFilter';
import { FilterContext, useFilter, useFilterContext } from './hooks';
import NumericFilter from './NumericFilter';
import SelectFilter from './SelectFilter';
import ToggleFilter from './ToggleFilter';
import { Filters } from './types';
import Spinner from '../spinner/Spinner';

export type FilterProps = {
    /**
     * Configuration object describing which filter types to use as well as their options.
     */
    filters: Filters;

    /**
     * Callback function invoked when a filter change is applied or when the filters are cleared.
     */
    onChange: (result: { queryString: string; currentFilterState: Filters }) => void;

    /**
     *
     */
    isLoading?: boolean;

    enableV2Query?: boolean;

    size?: 'small' | 'regular';

    menuPosition?: 'left' | 'center' | 'right';
};

function Filter({ filters, onChange, isLoading, enableV2Query, size = 'regular', menuPosition = 'right' }: FilterProps): JSX.Element {
    const filterInstance = useFilter(filters, enableV2Query);

    useEffect(() => {
        onChange(filterInstance.result);
    }, [filterInstance.result.queryString]);

    return (
        <div>
            <FilterContext.Provider value={filterInstance}>
                <Menu>
                    <Menu.Button>
                        <button
                            className={`relative w-fit inline-flex justify-center items-center focus:outline-none focus:ring-2 disabled:cursor-default disabled:opacity-50 focus:ring-indigo-500 border-gray-300  dark:text-white/[0.87] text-gray-700 bg-white dark:bg-white/[0.12] dark:hover:bg-white/[0.09] hover:bg-gray-50 dark:disabled:hover:bg-white/[0.12] disabled:hover:bg-white border dark:border-white/[0.12] shadow-sm px-3 ${
                                size === 'regular' ? 'py-1.5' : 'py-1'
                            } rounded-md`}
                        >
                            <label className={`font-medium ${size === 'regular' ? 'text-sm' : 'text-xs'} leading-4 mr-1`}>Filter</label>
                            {filterInstance.activeSectionCount === 0 ? (
                                <Badgev2 color="gray" shape="rounded" size="small" label={'0'} />
                            ) : (
                                <Badgev2 color="indigo" shape="rounded" size="small" label={filterInstance.activeSectionCount.toString()} />
                            )}
                            <ChevronDownIcon className="h-5 w-5 ml-2 -mr-1" />
                        </button>
                    </Menu.Button>
                    <Menu.Body position={menuPosition}>
                        <div className="relative">
                            {isLoading && (
                                <div className="absolute top-0 left-0 h-full w-full bg-white/[0.65] flex items-center justify-center">
                                    <Spinner />
                                </div>
                            )}
                            <FilterMenu disabled={isLoading}>
                                {filters.map((filter, idx) => {
                                    return (
                                        <div className="border-b dark:border-white/[0.12] last:border-b-0" key={idx}>
                                            {filter.type === 'checkbox' && <CheckboxFilter title={filter.title} id={filter.id} />}
                                            {filter.type === 'numeric' && <NumericFilter title={filter.title} id={filter.id} />}
                                            {filter.type === 'select' && <SelectFilter title={filter.title} id={filter.id} />}
                                            {filter.type === 'toggle' && <ToggleFilter title={filter.title} id={filter.id} />}
                                            {filter.type === 'date' && <DateFilter title={filter.title} id={filter.id} />}
                                        </div>
                                    );
                                })}
                            </FilterMenu>
                        </div>
                    </Menu.Body>
                </Menu>
            </FilterContext.Provider>
        </div>
    );
}

function FilterMenu({ children, disabled }: { children: ReactNode | ReactNode[]; disabled?: boolean }): JSX.Element {
    const filterInstance = useFilterContext();
    useEffect(() => {
        return (): void => {
            filterInstance.reset();
        };
    }, []);
    return (
        <div className={`min-w-[285px] ${disabled ? 'pointer-events-none' : ''}`}>
            {children}
            <div className="flex justify-between p-2 dark:bg-white/[0.04] bg-gray-100">
                {/* <Menu.Button> */}
                <Button
                    type="secondary"
                    label="Clear"
                    size="xs"
                    onClick={(): void => {
                        filterInstance.clear();
                        // onChange(undefined);
                        // setOpen(false);
                    }}
                />
                {/* </Menu.Button> */}
                <Menu.Button>
                    <Button
                        type="primary"
                        label="Apply"
                        size="xs"
                        onClick={(): void => {
                            filterInstance.apply();
                        }}
                    />
                </Menu.Button>
            </div>
        </div>
    );
}

export default Filter;
